import { createTextEvidenceFromLine, decodeTextImage, getBoldGlyphAtlas, getDefaultGlyphAtlas, TRAILING_DOUBLE_DOT_TOKEN, validateTextEvidence, } from 'maplestory-glyph-decoder';
import { assertCanonicalOwlTarget } from './canonical.js';
import { getOwlTextTarget } from './targets.js';
import { prepareOwlScreenshot } from './prepare.js';
import { decodeOwlIntegerSlots, decodeOwlTotalCountSlots } from './integer-slots.js';
import { decodeOwlPriceSlots } from './price-slots.js';
import { decodeOwlPageIndicator } from './page-indicator.js';
function optionsFor(target, options) {
    const specific = options.cellOptions?.[target.key] ?? {};
    const tokenSpecs = target.key === 'id'
        ? [TRAILING_DOUBLE_DOT_TOKEN]
        : undefined;
    const common = {
        ...options,
        profile: target.profile,
        charset: target.charset,
        tokenSpecs,
        // A player name cannot contain a literal period. Period glyphs are loaded
        // only so the trailing ".." token can match them as one unit.
        denylist: target.key === 'id' ? '.' : undefined,
        // Canonical row targets use their crop edges as logical text boundaries.
        fixedCropBounds: target.rowIndex !== undefined,
        direction: target.direction,
        startSearchPad: target.key === 'id' ? 0 : undefined,
        // Left-aligned ID/store-name cells use the soft-ink tail for anchorless
        // endings. Numeric row cells bypass this generic text decoder and use
        // fixed right-aligned integer slots.
        endEstimateBasis: target.key === 'id' || target.key === 'storeName' ? 'ink' : undefined,
        ...specific,
        // `auto` at the target level means the screenshot-wide variant already
        // detected during preparation, not a fresh mixed-atlas decode.
        ...(specific.atlasVariant === 'auto'
            && (options.atlasVariant === 'cleartype-rgb' || options.atlasVariant === 'cleartype-bgr')
            ? { atlasVariant: options.atlasVariant }
            : {}),
    };
    if (target.key !== 'itemName')
        return common;
    // The bold Owl item-name font is a separate face with different glyph
    // advances and templates, but follows the detected/requested ClearType
    // subpixel order just like the regular fields.
    const variant = common.atlasVariant === 'cleartype-bgr' ? 'cleartype-bgr' : 'cleartype-rgb';
    return {
        ...common,
        profile: 'red',
        charset: target.charset,
        atlas: getBoldGlyphAtlas({ variant, charset: target.charset }),
        atlasVariant: variant,
    };
}
function strictShapeValidation(target, decoded, text, minF1, atlas) {
    const evidence = createTextEvidenceFromLine(decoded.line, target.image.height, decoded.lineRect.y);
    return validateTextEvidence(evidence, text, {
        atlas,
        atlasVariant: decoded.atlasVariant,
        expectedWidth: target.image.width,
        expectedCropHeight: target.image.height,
        expectedLineY: decoded.lineRect.y,
        requireExactAnchors: true,
        maxAnchorPixelMismatches: 0,
        maxExtraAnchorPixels: 0,
        minF1,
        maxFalsePositiveRate: 1,
        maxFalseNegativeRate: 1,
        maxExtraInkPixels: 0,
        maxTextLength: 100,
    });
}
function strictShapeMatches(target, decoded, text, minF1, atlas) {
    return minF1 <= 0 || strictShapeValidation(target, decoded, text, minF1, atlas).matches;
}
function normalizePlayerName(target, decoded) {
    if (strictShapeMatches(target, decoded, decoded.text, 0.95, undefined))
        return decoded.text;
    // End padding can occasionally admit one anchorless i/I/l after
    // an otherwise complete untruncated name. Keep the longest prefix whose full
    // shape explains all observed ink; a real trailing i/I/l still validates and
    // is therefore never removed.
    if (!decoded.text.endsWith('..')) {
        for (let length = decoded.text.length - 1; length > 0; length--) {
            const candidate = decoded.text.slice(0, length);
            if (strictShapeMatches(target, decoded, candidate, 0.95, undefined))
                return candidate;
        }
    }
    return '';
}
/** Resolve the only shape-identical pair in the generated bold atlas.
 *
 * Uppercase I and lowercase l render identically. MapleStory item words use
 * title case, lowercase, or all-uppercase abbreviations. The atlas now prefers
 * lowercase l, so a word-initial ambiguous glyph is restored to uppercase I;
 * internal occurrences remain lowercase. This preserves initial Iron/INT/R.I.P.
 * forms while making the more common lowercase glyph the raw-decoder default.
 */
function normalizeBoldItemName(text) {
    return text.replace(/[^ ]+/gu, (word) => {
        const chars = [...word];
        return chars.map((char, index) => (char === 'l' && (index === 0 || !/[A-Za-z]/u.test(chars[index - 1])) ? 'I' : char)).join('');
    });
}
function decodeStoreName(target, decoded, decodeOptions, options) {
    const minF1 = options.minStoreNameF1 ?? 0.95;
    if (minF1 <= 0)
        return decoded.text;
    const charset = decodeOptions.charset ?? target.charset;
    const atlas = decodeOptions.atlas ?? getDefaultGlyphAtlas({
        variant: decoded.atlasVariant,
        charset,
    });
    return strictShapeMatches(target, decoded, decoded.text, minF1, atlas) ? decoded.text : '';
}
function decodeTarget(target, options) {
    const decodeOptions = optionsFor(target, options);
    if (target.key === 'totalCount') {
        return {
            decoded: null,
            text: decodeOwlTotalCountSlots(target.image, {
                atlas: decodeOptions.atlas,
                atlasVariant: decodeOptions.atlasVariant,
            }),
        };
    }
    if (target.key === 'pageIndicator') {
        return {
            decoded: null,
            text: decodeOwlPageIndicator(target.image, {
                atlas: decodeOptions.atlas,
                atlasVariant: decodeOptions.atlasVariant,
                beamSize: decodeOptions.beamSize,
                branchLimit: decodeOptions.branchLimit,
                confidenceFloor: decodeOptions.confidenceFloor,
            }),
        };
    }
    if (target.key === 'price') {
        return {
            decoded: null,
            text: decodeOwlPriceSlots(target.image, {
                atlas: decodeOptions.atlas,
                atlasVariant: decodeOptions.atlasVariant,
                billionOverlapField: target.billionOverlapField,
            }),
        };
    }
    if (target.key === 'bundle' || target.key === 'quantity') {
        return {
            decoded: null,
            text: decodeOwlIntegerSlots(target.image, {
                atlas: decodeOptions.atlas,
                atlasVariant: decodeOptions.atlasVariant,
                billionOverlapField: target.billionOverlapField,
            }),
        };
    }
    const decoded = decodeTextImage(target.image, decodeOptions);
    if (!decoded?.text)
        return { decoded, text: '' };
    if (target.key === 'id')
        return { decoded, text: normalizePlayerName(target, decoded) };
    if (target.key === 'storeName')
        return { decoded, text: decodeStoreName(target, decoded, decodeOptions, options) };
    if (target.key === 'itemName') {
        const itemName = normalizeBoldItemName(decoded.text);
        return {
            decoded,
            text: strictShapeMatches(target, decoded, itemName, options.minItemNameF1 ?? 0.95, decodeOptions.atlas)
                ? itemName
                : '',
        };
    }
    return { decoded, text: decoded.text };
}
export function decodeOwlTextTarget(target, options = {}) {
    assertCanonicalOwlTarget(target);
    return decodeTarget(target, options).text;
}
function positiveInteger(text) {
    const digits = text.replace(/\D+/gu, '');
    if (!digits)
        return undefined;
    const value = Number.parseInt(digits, 10);
    return Number.isSafeInteger(value) ? value : undefined;
}
function pageValues(text) {
    const match = /([0-9]+)\s*\/\s*([0-9]+)/u.exec(text);
    if (!match)
        return {};
    return { page: Number.parseInt(match[1], 10), totalPages: Number.parseInt(match[2], 10) };
}
export function transcribePreparedOwlScreenshot(prepared, options = {}) {
    const effectiveOptions = options.atlasVariant === undefined || options.atlasVariant === 'auto'
        ? { ...options, atlasVariant: prepared.atlasVariant }
        : options;
    const itemName = decodeOwlTextTarget(getOwlTextTarget(prepared, 'itemName'), effectiveOptions);
    const totalCount = decodeOwlTextTarget(getOwlTextTarget(prepared, 'totalCount'), effectiveOptions);
    const pageIndicator = decodeOwlTextTarget(getOwlTextTarget(prepared, 'pageIndicator'), effectiveOptions);
    const rows = [];
    const keys = ['id', 'storeName', 'bundle', 'price', 'quantity'];
    for (const preparedRow of prepared.rows) {
        const values = Object.fromEntries(keys.map((key) => [key, decodeOwlTextTarget(getOwlTextTarget(prepared, key, preparedRow.index), effectiveOptions)]));
        const row = { index: preparedRow.index, ...values };
        if (keys.some((key) => row[key]))
            rows.push(row);
    }
    return {
        layout: prepared.layout,
        itemName,
        totalCount,
        totalRows: positiveInteger(totalCount),
        pageIndicator,
        ...pageValues(pageIndicator),
        rows,
    };
}
export function transcribeOwlScreenshotInternal(source, options = {}) {
    const prepared = prepareOwlScreenshot(source, options);
    return prepared ? transcribePreparedOwlScreenshot(prepared, options) : null;
}
//# sourceMappingURL=transcribe.js.map